import {
  BeneficiarySubscriptionModel,
} from '../../models/beneficiaries/BeneficiarySubscription.model';
import { manageError } from '../errors';
import {
  BeneficiaryModel,
  HydratedSubscriptionEntry,
  SubscriptionEntries,
} from '../../models/beneficiaries/Beneficiary.model';
import { getContractSpecification } from '../contracts/contractTypes.util';
import { ContractSpecificationModel } from '../../models/contracts/AgencyContract.model';
import { OpenProductName, ProductContractVariant } from '../../models/products/Products.model';

export function isSubscriptionOfProduct<P extends OpenProductName>(
  sub: BeneficiarySubscriptionModel<any, any>,
  product: P,
): sub is BeneficiarySubscriptionModel<P, ProductContractVariant<P>> {
  return sub.openProductName === product;
}

export function isSubscriptionOfContract<P extends OpenProductName, C extends ProductContractVariant<P>>(
  sub: BeneficiarySubscriptionModel<any, any>,
  product: P,
  contract: C,
): sub is BeneficiarySubscriptionModel<P, C> {
  return sub.openProductName === product && sub.contractVariant === contract;
}

export function assertIsSubscriptionOfProduct<P extends OpenProductName>(
  sub: BeneficiarySubscriptionModel<any, any>,
  product: P,
): asserts sub is BeneficiarySubscriptionModel<P, ProductContractVariant<P>> {
  if (!isSubscriptionOfProduct(sub, product)) {
    manageError({
      code: 'failed-precondition', message: `Subscription ${sub.uid} is not associated with product ${product}`,
    });
  }
}

export function assertIsSubscriptionOfContract<P extends OpenProductName, C extends ProductContractVariant<P>>(
  sub: BeneficiarySubscriptionModel<any, any>,
  product: P,
  contract: C,
): asserts sub is BeneficiarySubscriptionModel<P, C> {
  if (!isSubscriptionOfContract(sub, product, contract)) {
    manageError({
      code: 'failed-precondition',
      message: `Subscription ${sub.uid} is not associated with product ${product} and contract ${contract as string}`,
    });
  }
}

// SUBSCRIPTION ENTRIES - BENEFICIARY.subscriptions
export function getHydratedSubscriptionEntries(subscriptions: SubscriptionEntries | undefined): HydratedSubscriptionEntry[] {
  return Object.entries(subscriptions ?? {}).flatMap(([subKey, subEntries]) => {
    const [product, contract] = subKey.split(':') as [OpenProductName, ProductContractVariant];
    const contractSpecification: ContractSpecificationModel | null = getContractSpecification(product, contract);
    return Object.entries(subEntries).map(([subscriptionId, subEntry]) => ({
      subscriptionId,
      product,
      contract,
      contractSpecification,
      ...subEntry,
    }));
  });
}

export function getProductSubscriptionEntry<P extends OpenProductName>(subscriptions: SubscriptionEntries | undefined, product: P): HydratedSubscriptionEntry<P> | undefined {
  const hydratedSubscriptionEntries: HydratedSubscriptionEntry[] = getHydratedSubscriptionEntries(subscriptions);
  const productEntries: HydratedSubscriptionEntry<P>[] = hydratedSubscriptionEntries.filter(entry => entry.product === product) as HydratedSubscriptionEntry<P>[];
  return productEntries.find((entry) => entry.status === 'ACTIVE') ?? productEntries[0];
}

export function getBeneficiaryHydratedSubscriptionEntry(beneficiary: BeneficiaryModel, subscriptionId: string): HydratedSubscriptionEntry | undefined {
  const subscriptionEntries: HydratedSubscriptionEntry[] = getHydratedSubscriptionEntries(beneficiary.subscriptions);
  return subscriptionEntries.find(subEntry => subEntry.subscriptionId === subscriptionId);
}

export function getSubscriptionEntryFromAgencyContract(beneficiary: BeneficiaryModel, agencyContractId: string, triggerError: true): HydratedSubscriptionEntry
export function getSubscriptionEntryFromAgencyContract(beneficiary: BeneficiaryModel, agencyContractId: string, triggerError: false): HydratedSubscriptionEntry | undefined
export function getSubscriptionEntryFromAgencyContract(beneficiary: BeneficiaryModel, agencyContractId: string, triggerError: boolean): HydratedSubscriptionEntry | undefined {
  const subscriptionEntries = getHydratedSubscriptionEntries(beneficiary.subscriptions)
    .filter(subEntry => subEntry.agencyContractId === agencyContractId);

  // Because of deactivation and reactivation we can have multiple subscriptions linked to an agency contract, we chose to return first ACTIVE subscription entry
  // Or if none was found, the first subscription entry
  const subscriptionEntry = subscriptionEntries
    .find(subEntry => subEntry.status === 'ACTIVE') ?? subscriptionEntries[0];

  if (!subscriptionEntry && triggerError) {
    manageError({
      code: 'not-found',
      message: `Subscription entry for beneficiary ${beneficiary.uid} not found for agency contract ${agencyContractId}`,
    });
  }

  return subscriptionEntry;
}